import React, { useState } from 'react';
import { X, Star, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Contact } from '../types';

const PHONE_TYPE_OPTIONS = [
  { id: 2, label: 'Work Landline' },
  { id: 1, label: 'Work Mobile' },
  { id: 4, label: 'Personal Mobile' },
  { id: 3, label: 'Home Mobile' },
  { id: 5, label: 'Duty Phone' },
  { id: 6, label: 'Ops Center' },
  { id: 7, label: 'Fax' },
  { id: 8, label: 'Other' },
];

interface PhoneRow {
  phone_type_id: number;
  phone_number: string;
  extension: string;
  is_preferred: boolean;
  sort_order: number;
}

interface Props {
  contact: Contact;
  onClose: () => void;
}

const EditContactModal: React.FC<Props> = ({ contact, onClose }) => {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    first_name:        contact.first_name ?? '',
    last_name:         contact.last_name ?? '',
    title:             contact.title ?? '',
    office_group:      contact.office_group ?? '',
    email_primary:     contact.email_primary ?? '',
    classification_id: contact.classification_id ?? 1,
  });

  const initPhones = (): PhoneRow[] => {
    const existing = contact.phones ?? [];
    if (existing.length > 0) {
      return existing.map((p, i) => ({
        phone_type_id: p.phone_type_id ?? 2,
        phone_number: p.phone_number ?? '',
        extension: p.extension ?? '',
        is_preferred: p.is_preferred ?? i === 0,
        sort_order: i,
      }));
    }
    return [{ phone_type_id: 2, phone_number: '', extension: '', is_preferred: true, sort_order: 0 }];
  };

  const [phones, setPhones] = useState<PhoneRow[]>(initPhones);

  const addPhone = () => {
    setPhones((prev) => [
      ...prev,
      { phone_type_id: 1, phone_number: '', extension: '', is_preferred: prev.length === 0, sort_order: prev.length },
    ]);
  };

  const removePhone = (idx: number) => {
    setPhones((prev) => {
      const next = prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, sort_order: i }));
      if (next.length > 0 && !next.some((p) => p.is_preferred)) {
        next[0].is_preferred = true;
      }
      return next;
    });
  };

  const setPreferred = (idx: number) => {
    setPhones((prev) => prev.map((p, i) => ({ ...p, is_preferred: i === idx })));
  };

  const updatePhone = (idx: number, field: keyof PhoneRow, value: string | number | boolean) => {
    setPhones((prev) => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const changes: Record<string, unknown> = {};
      if (form.first_name.trim() !== (contact.first_name ?? ''))     changes.first_name        = form.first_name.trim();
      if (form.last_name.trim() !== (contact.last_name ?? ''))       changes.last_name         = form.last_name.trim();
      if (form.title !== (contact.title ?? ''))                      changes.title             = form.title || null;
      if (form.office_group !== (contact.office_group ?? ''))        changes.office_group      = form.office_group || null;
      if (form.email_primary !== (contact.email_primary ?? ''))      changes.email_primary     = form.email_primary || null;
      if (form.classification_id !== contact.classification_id)      changes.classification_id = form.classification_id;

      const cleanPhones = phones
        .filter((p) => p.phone_number.trim())
        .map((p, i) => ({ ...p, phone_number: p.phone_number.trim(), sort_order: i }));

      changes.phones = cleanPhones;

      await api.updateContact(contact.id, changes);
      qc.invalidateQueries({ queryKey: ['contact', contact.id] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="modal"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ maxWidth: 540, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexShrink: 0 }}>
          <h3 style={{ margin: 0 }}>Edit Contact</h3>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label className="field-label">First Name *</label>
              <input className="field-input" type="text" value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </div>
            <div>
              <label className="field-label">Last Name *</label>
              <input className="field-input" type="text" value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Title</label>
            <input className="field-input" type="text" value={form.title}
              placeholder="e.g. Emergency Management Director"
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Office / Group</label>
            <input className="field-input" type="text" value={form.office_group}
              placeholder="e.g. Operations Division"
              onChange={(e) => setForm({ ...form, office_group: e.target.value })} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Primary Email</label>
            <input className="field-input" type="email" value={form.email_primary}
              onChange={(e) => setForm({ ...form, email_primary: e.target.value })} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="field-label">Classification</label>
            <select className="field-input" value={form.classification_id}
              onChange={(e) => setForm({ ...form, classification_id: Number(e.target.value) })}>
              <option value={1}>Standard</option>
              <option value={2}>Restricted</option>
            </select>
          </div>

          {/* Phone section */}
          <div style={{ marginBottom: 16 }}>
            <label className="field-label">Phone Numbers</label>
            {phones.map((phone, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <select
                  value={phone.phone_type_id}
                  onChange={(e) => updatePhone(idx, 'phone_type_id', Number(e.target.value))}
                  style={{ padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12, flexShrink: 0 }}
                >
                  {PHONE_TYPE_OPTIONS.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  className="field-input"
                  style={{ flex: 1 }}
                  placeholder="212-555-0100"
                  value={phone.phone_number}
                  onChange={(e) => updatePhone(idx, 'phone_number', e.target.value)}
                />
                <input
                  type="text"
                  className="field-input"
                  style={{ width: 64, flexShrink: 0 }}
                  placeholder="Ext."
                  value={phone.extension}
                  onChange={(e) => updatePhone(idx, 'extension', e.target.value)}
                />
                <button
                  title={phone.is_preferred ? 'Primary' : 'Set as primary'}
                  onClick={() => setPreferred(idx)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: phone.is_preferred ? 'var(--amber-text)' : 'var(--gray-300)', flexShrink: 0,
                  }}
                >
                  <Star size={15} fill={phone.is_preferred ? '#f59e0b' : 'none'} />
                </button>
                <button
                  onClick={() => removePhone(idx)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: '4px', flexShrink: 0 }}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPhone}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <Plus size={13} /> Add Phone
            </button>
          </div>
        </div>

        {error && <p style={{ color: 'var(--red)', fontSize: 12, margin: '8px 0 0', flexShrink: 0 }}>{error}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={saving || !form.first_name.trim() || !form.last_name.trim()}
            onClick={handleSubmit}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditContactModal;
